import React, { useEffect, useState } from 'react';
import { MessageSquare, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';

/**
 * Lets clients + providers opt in/out of SMS notifications and set the mobile
 * number we send to. Phone is validated + normalised server-side so the stored
 * value is always E.164. Empty phone with opt-in on is fine, we just skip
 * sends for them until they fill it in.
 */
export function NotificationPreferencesCard() {
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/auth/me')
      .then((res) => {
        if (cancelled) return;
        setPhone(res.data.phone || '');
        setSmsOptIn(res.data.sms_opt_in !== false);
      })
      .catch(() => { /* silently fall back to defaults */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/auth/me/notifications', {
        phone: phone.trim(),
        sms_opt_in: smsOptIn,
      });
      setPhone(res.data.phone || '');
      setSmsOptIn(res.data.sms_opt_in !== false);
      setDirty(false);
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-stone-200 p-6 flex items-center gap-3 text-sm text-stone-400" data-testid="sms-prefs-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading notification preferences...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6" data-testid="notification-preferences-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="rounded-lg bg-[#FFF8E7] p-2 border border-[#D4AF37]/30">
          <MessageSquare className="h-5 w-5 text-[#D4AF37]" />
        </div>
        <div>
          <h3 className="font-heading text-lg text-[#1A1A1A]">SMS notifications</h3>
          <p className="text-sm text-stone-500 mt-0.5">Get a text when your booking is confirmed, alongside the usual email.</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="sms-phone" className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">Mobile number</label>
          <Input
            id="sms-phone"
            type="tel"
            placeholder="07123 456789 or +44 7123 456789"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setDirty(true); }}
            disabled={saving}
            className="input-zuriya"
            data-testid="sms-phone-input"
          />
          <p className="text-xs text-stone-400 mt-1.5">UK mobile. Leave empty to turn off SMS.</p>
        </div>

        <label className="flex items-center justify-between gap-3 cursor-pointer rounded-lg border border-stone-200 p-3 bg-stone-50">
          <div>
            <p className="text-sm font-medium text-[#1A1A1A]">Send me booking-confirmation texts</p>
            <p className="text-xs text-stone-500 mt-0.5">You can always text STOP to opt out.</p>
          </div>
          <Switch
            checked={smsOptIn}
            onCheckedChange={(v) => { setSmsOptIn(v); setDirty(true); }}
            data-testid="sms-opt-in-switch"
          />
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          {!dirty && !saving && (
            <span className="text-xs text-stone-400 flex items-center gap-1" data-testid="sms-prefs-saved">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
              Saved
            </span>
          )}
          <Button
            className="btn-primary"
            onClick={handleSave}
            disabled={!dirty || saving}
            data-testid="sms-prefs-save-btn"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
